import { create } from "zustand";

export interface ProcessPlan {
  id: string;
  processName: string;
  sequence: number;
  machineName?: string;
  unit: "SHEET" | "PCS";
  minWasteQty: number;
  wastePercent: number;
  targetQty: number;
  wasteQty: number;
  totalQty: number;
  startDate?: string;
  endDate?: string;
}

export interface MaterialPlan {
  id: string;
  materialCode: string;
  materialName: string;
  specs: string;
  unit: string;
  normPerUnit: number;
  issueQty: number;
  wasteQty: number;
  totalIssueQty: number;
  status: string;
}

interface ProductionOrderState {
  orderProducts: any[];
  processes: ProcessPlan[];
  materials: MaterialPlan[];
  materialPlans: any[]; // Extended for Phase 2
  printLayouts: any[];
  qtyPlan: number;
  qtyAllowance: number;
  prepressPcsPerSheet: number;

  // Actions
  initialize: (data: any) => void;
  updateQtyPlan: (qty: number) => void;
  updatePrepress: (pcsPerSheet: number) => void;
  updateProcess: (id: string, updates: Partial<ProcessPlan>) => void;
  recalculate: () => void;

  // Material Planning Actions
  addMaterialPlan: (plan: any) => void;
  updateMaterialPlan: (id: string, updates: any) => void;
  removeMaterialPlan: (id: string) => void;

  // Print Layout Actions
  addPrintLayout: (layout: any) => void;
  updatePrintLayout: (id: string, updates: any) => void;
  removePrintLayout: (id: string) => void;
}

export const useProductionOrderStore = create<ProductionOrderState>(
  (set, get) => ({
    orderProducts: [],
    processes: [],
    materials: [],
    materialPlans: [],
    printLayouts: [],
    qtyPlan: 0,
    qtyAllowance: 0,
    prepressPcsPerSheet: 1,

    initialize: (data) => {
      set({
        orderProducts: data.orderProducts || [],
        processes: data.processes || [],
        materials: data.materials || [],
        materialPlans: data.materialPlans || [],
        printLayouts: data.printLayouts || [],
        qtyPlan: data.qtyPlan || 0,
        qtyAllowance: data.qtyAllowance || 0,
        prepressPcsPerSheet: data.prepressPcsPerSheet || 1,
      });
      get().recalculate();
    },

    updateQtyPlan: (qty) => {
      set({ qtyPlan: qty });
      get().recalculate();
    },

    updatePrepress: (pcsPerSheet) => {
      set({ prepressPcsPerSheet: pcsPerSheet });
      get().recalculate();
    },

    updateProcess: (id, updates) => {
      set((state) => ({
        processes: state.processes.map((p) =>
          p.id === id ? { ...p, ...updates } : p,
        ),
      }));
      get().recalculate();
    },

    recalculate: () => {
      const { processes, materials, qtyPlan, prepressPcsPerSheet } = get();

      // Target is based on sheet if prepress is used
      const totalTargetSheets = Math.ceil(qtyPlan / prepressPcsPerSheet);

      let currentRequirement = totalTargetSheets;

      // Recalculate processes backwards (from finish to start) to accumulate waste
      const updatedProcesses = [...processes]
        .reverse()
        .map((p) => {
          // Target qty is what's required by the NEXT process (or final product)
          const pTarget = currentRequirement;

          // Calculate waste
          const calculatedWaste = Math.ceil(pTarget * (p.wastePercent / 100));
          const waste = Math.max(p.minWasteQty || 0, calculatedWaste);
          const total = pTarget + waste;

          currentRequirement = total; // Required input for THIS process is the target for PREVIOUS

          return {
            ...p,
            targetQty: pTarget,
            wasteQty: waste,
            totalQty: total,
          };
        })
        .reverse();

      // Total sheet requirement at the start of printing
      const totalSheetsRequired =
        updatedProcesses.length > 0
          ? updatedProcesses[0].totalQty
          : totalTargetSheets;

      // Recalculate materials
      const updatedMaterials = materials.map((m) => {
        // Simplistic mapping: if it's paper, tie to totalSheetsRequired
        const issueQty = totalSheetsRequired * m.normPerUnit;
        const wasteQty = Math.ceil(issueQty * 0.05); // dummy 5% waste for material for now
        return {
          ...m,
          issueQty,
          wasteQty,
          totalIssueQty: issueQty + wasteQty,
        };
      });

      // Recalculate material plans
      const { materialPlans, printLayouts } = get();
      const updatedMaterialPlans = materialPlans.map((mp) => {
        // Automatically sync sheetsRequired with the layout's required sheets (using the overall totalSheetsRequired for now, or printLayout if available)
        let layoutSheets = totalSheetsRequired;
        if (mp.printLayoutId) {
          const layout = printLayouts.find((l) => l.id === mp.printLayoutId);
          if (layout && layout.totalSheetsRequired) {
            layoutSheets = layout.totalSheetsRequired;
          }
        }
        const sheetsRequired = mp.isCustomQuantity
          ? mp.sheetsRequired || 0
          : layoutSheets;
        const wastageSheets = mp.wastageSheets || 0;
        const totalSheets = sheetsRequired + wastageSheets;
        return {
          ...mp,
          sheetsRequired,
          totalSheets,
        };
      });

      set({
        processes: updatedProcesses,
        materials: updatedMaterials,
        materialPlans: updatedMaterialPlans,
      });
    },

    // --- Material Planning specific actions ---
    addMaterialPlan: (plan) => {
      set((state) => ({
        materialPlans: [...state.materialPlans, plan],
      }));
      get().recalculate();
    },

    updateMaterialPlan: (id, updates) => {
      set((state) => ({
        materialPlans: state.materialPlans.map((mp) =>
          mp.id === id ? { ...mp, ...updates } : mp,
        ),
      }));
      get().recalculate();
    },

    removeMaterialPlan: (id) => {
      set((state) => ({
        materialPlans: state.materialPlans.filter((mp) => mp.id !== id),
      }));
      get().recalculate();
    },

    // --- Print Layout specific actions ---
    addPrintLayout: (layout) => {
      set((state) => ({
        printLayouts: [...state.printLayouts, layout],
      }));
      get().recalculate();
    },

    updatePrintLayout: (id, updates) => {
      set((state) => ({
        printLayouts: state.printLayouts.map((l) =>
          l.id === id ? { ...l, ...updates } : l,
        ),
      }));
      get().recalculate();
    },

    removePrintLayout: (id) => {
      set((state) => ({
        printLayouts: state.printLayouts.filter((l) => l.id !== id),
      }));
      get().recalculate();
    },
  }),
);
